import { useState, useEffect } from 'react';

export interface Supplement {
  name: string;
  price: number;
}

export interface FavoriteItem {
  id: string;
  name: string;
  ingredients: string;
  price: string;
  category: string;
  size?: 'normal' | 'grande';
  supplements?: Supplement[];
  removedIngredients?: string[];
  quantity?: number;
  note?: string;
}

// Événement personnalisé pour synchroniser les favoris
const FAVORITES_STORAGE_KEY = 'pizzajo-favorites';
const FAVORITES_EVENT = 'favorites-updated';

// Fonction pour déclencher l'événement de mise à jour
const dispatchFavoritesUpdate = () => {
  window.dispatchEvent(new CustomEvent(FAVORITES_EVENT));
};

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);

  // Charger les favoris depuis localStorage
  const loadFavorites = () => {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (stored) {
      try {
        setFavorites(JSON.parse(stored));
      } catch (error) {
        console.error('Erreur lors du chargement des favoris:', error);
        setFavorites([]);
      }
    }
  };

  // Charger au montage
  useEffect(() => {
    loadFavorites();
  }, []);

  // Écouter les changements de favoris
  useEffect(() => {
    const handleFavoritesUpdate = () => {
      loadFavorites();
    };

    window.addEventListener(FAVORITES_EVENT, handleFavoritesUpdate);
    
    return () => {
      window.removeEventListener(FAVORITES_EVENT, handleFavoritesUpdate);
    };
  }, []);

  const saveFavorites = (items: FavoriteItem[]) => {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(items));
    setFavorites(items);
    // Déclencher l'événement pour synchroniser tous les composants
    dispatchFavoritesUpdate();
  };

  const addFavorite = (item: FavoriteItem, onSuccess?: (itemName: string) => void) => {
    const exists = favorites.some(f => f.id === item.id);
    if (!exists) {
      const newFavorites = [...favorites, { ...item, quantity: item.quantity || 1 }];
      saveFavorites(newFavorites);
      if (onSuccess) {
        onSuccess(item.name);
      }
    }
  };

  const removeFavorite = (id: string, onSuccess?: (itemName: string) => void) => {
    const item = favorites.find(f => f.id === id);
    const newFavorites = favorites.filter(f => f.id !== id);
    saveFavorites(newFavorites);
    if (onSuccess && item) {
      onSuccess(item.name);
    }
  };

  const isFavorite = (id: string) => {
    return favorites.some(f => f.id === id);
  };

  const clearFavorites = () => {
    saveFavorites([]);
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) return;
    if (quantity > 20) return;
    const newFavorites = favorites.map(f =>
      f.id === id ? { ...f, quantity } : f
    );
    saveFavorites(newFavorites);
  };

  return {
    favorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    clearFavorites,
    updateQuantity,
  };
}